import type { ReactNode } from 'react';
import { Box, Flex, HStack, Icon, Text, Tooltip, VStack } from '@chakra-ui/react';
import {
  Check,
  Clock,
  Cloud,
  CloudOff,
  Database,
  Layers,
  Loader,
  Send,
  TriangleAlert,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import type { BridgeVerdict } from './diagnoses';
import { testIds } from './testIds';

// The Unit Analytics stat recipe: tinted icon square, sm/medium label, 2xl
// value. No caption line — a stat that needs explaining tints its icon square
// amber instead (with a tooltip), so the eye reads "something here needs a
// look" without a wall of small text under every number.
function Stat({
  icon,
  colour,
  warn = false,
  title,
  value,
  tooltip,
}: {
  icon: React.ElementType;
  colour: string;
  warn?: boolean;
  title: ReactNode;
  value: string;
  tooltip?: string;
}) {
  const content = (
    <HStack spacing={3} align="center">
      <Box
        p={2}
        borderRadius="md"
        bg={warn ? 'orange.50' : `${colour}.50`}
        color={warn ? 'orange.600' : `${colour}.600`}
        flexShrink={0}
      >
        <Icon as={icon} boxSize={5} />
      </Box>
      <VStack align="start" spacing={0}>
        <Text fontSize="sm" color="fg.muted" fontWeight="medium" whiteSpace="nowrap">
          {title}
        </Text>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          color="fg.default"
          lineHeight="1.15"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Text>
      </VStack>
    </HStack>
  );

  return tooltip === undefined ? content : <Tooltip label={tooltip}>{content}</Tooltip>;
}

function SystemPanel({
  icon,
  label,
  children,
}: {
  icon: typeof Database;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      flexShrink={0}
      borderWidth="1px"
      borderColor="border.default"
      bg="bg.surface"
      borderRadius="md"
      px={4}
      py={3}
    >
      <HStack spacing={1.5} mb={3}>
        <Icon as={icon} boxSize={iconSize.sm} color="fg.muted" />
        <Text
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="0.05em"
          textTransform="uppercase"
          color="fg.muted"
        >
          {label}
        </Text>
      </HStack>
      <HStack spacing={6} align="center">
        {children}
      </HStack>
    </Box>
  );
}

// The link between the two boxes: a small verdict box on a short connector, so
// the reconciliation reads as the relationship between Database and Queue,
// not a peer of either. Grey and quiet when healthy; colour only on real
// divergence or an unreachable queue.
function VerdictLink({ verdict, dbActive }: { verdict: BridgeVerdict; dbActive: number }) {
  const idle = verdict.kind === 'match' && verdict.dbActive === 0 && verdict.sqsTotal === 0;
  const style =
    verdict.kind === 'unknown'
      ? { border: 'orange.200', bg: 'orange.50', fg: 'orange.700', icon: CloudOff }
      : verdict.kind === 'mismatch'
        ? { border: 'orange.200', bg: 'orange.50', fg: 'orange.700', icon: TriangleAlert }
        : { border: 'border.default', bg: 'bg.canvas', fg: 'fg.muted', icon: Check };

  const title =
    verdict.kind === 'unknown' ? (
      <Trans>Unreachable</Trans>
    ) : verdict.kind === 'mismatch' ? (
      <Trans>Diverged</Trans>
    ) : (
      <Trans>In step</Trans>
    );

  const detail =
    verdict.kind === 'unknown' ? (
      <Trans>{dbActive} rows, queue unknown</Trans>
    ) : idle ? (
      <Trans>idle</Trans>
    ) : verdict.kind === 'mismatch' ? (
      <Trans>
        {verdict.dbActive} rows vs {verdict.sqsTotal} messages
      </Trans>
    ) : (
      <Trans>
        {verdict.dbActive} rows ≈ {verdict.sqsTotal} messages
      </Trans>
    );

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      align="center"
      gap={0}
      data-testid={testIds.bridge}
    >
      <Box
        display={{ base: 'none', lg: 'block' }}
        w={4}
        h="1px"
        bg={verdict.kind === 'match' ? 'border.default' : 'orange.200'}
      />
      <VStack
        spacing={0.5}
        borderWidth="1px"
        borderColor={style.border}
        bg={style.bg}
        borderRadius="md"
        px={3}
        py={1.5}
        minW={0}
      >
        <HStack spacing={1.5}>
          <Icon as={style.icon} boxSize={iconSize.xs} color={style.fg} />
          <Text fontSize="sm" fontWeight="bold" color={style.fg} whiteSpace="nowrap">
            {title}
          </Text>
        </HStack>
        <Text
          fontSize="xs"
          color={style.fg}
          opacity={0.85}
          whiteSpace="nowrap"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {detail}
        </Text>
      </VStack>
      <Box
        display={{ base: 'none', lg: 'block' }}
        w={4}
        h="1px"
        bg={verdict.kind === 'match' ? 'border.default' : 'orange.200'}
      />
    </Flex>
  );
}

export function HealthStrip({
  queued,
  generating,
  failedInWindow,
  stalledCount,
  sqs,
  verdict,
  mismatch,
}: {
  queued: number;
  generating: number;
  failedInWindow: number;
  // Tints the Translating icon square amber; the count itself surfaces in the
  // Needs attention strip, not here.
  stalledCount: number;
  sqs: { ok: true; visible: number; inFlight: number; delayed: number } | { ok: false };
  verdict: BridgeVerdict;
  // Sustained stranded/ghost divergence tints the matching icon squares.
  mismatch: boolean;
}) {
  return (
    <Box>
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="0.05em"
        textTransform="uppercase"
        color="fg.muted"
        mb={2}
      >
        <Trans>Pipeline</Trans>
      </Text>
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        justify="center"
        align="center"
        gap={4}
        data-testid={testIds.healthStrip}
      >
        <SystemPanel icon={Database} label={<Trans>Database</Trans>}>
          <Stat
            icon={Clock}
            colour="blue"
            warn={mismatch}
            title={<Trans>Queued</Trans>}
            value={String(queued)}
            tooltip={
              mismatch ? t`Some of these rows have no message waiting on the queue.` : undefined
            }
          />
          <Stat
            icon={Loader}
            colour="blue"
            warn={stalledCount > 0}
            title={<Trans>Translating</Trans>}
            value={String(generating)}
            tooltip={
              stalledCount > 0
                ? t`${stalledCount} of these runs started more than 15 minutes ago and may be stalled.`
                : undefined
            }
          />
          <Stat
            icon={TriangleAlert}
            colour={failedInWindow > 0 ? 'red' : 'gray'}
            title={<Trans>Failed (7d)</Trans>}
            value={String(failedInWindow)}
          />
        </SystemPanel>
        <VerdictLink verdict={verdict} dbActive={queued + generating} />
        <SystemPanel icon={Cloud} label={<Trans>Queue · AWS SQS</Trans>}>
          <Stat
            icon={Layers}
            colour="gray"
            warn={sqs.ok && mismatch}
            title={<Trans>Queue depth</Trans>}
            value={sqs.ok ? String(sqs.visible) : '—'}
            tooltip={
              !sqs.ok
                ? t`Could not read the queue.`
                : mismatch
                  ? t`This does not match the database's queued count.`
                  : t`Approximate — SQS counts are eventually consistent.`
            }
          />
          <Stat
            icon={Send}
            colour="gray"
            title={<Trans>In flight</Trans>}
            value={sqs.ok ? String(sqs.inFlight) : '—'}
            tooltip={
              !sqs.ok
                ? t`Could not read the queue.`
                : sqs.delayed > 0
                  ? t`${sqs.delayed} messages delayed.`
                  : undefined
            }
          />
        </SystemPanel>
      </Flex>
    </Box>
  );
}
